import styled from "styled-components";
import { Column, Row, SizedBox } from "../CommonBaseComponents";
import React, { useRef } from "react";
import { C3Text, C4Text } from "../text/CustomText";
import CustomInput from "./CustomInput";
import CustomIcon from "../image/CustomIcon";
import CustomTooltip from "../button/CustomTooltip";
import FullWidthButton from "../button/FullWidthButton";
import { Theme } from "../../Theme";
import Translate from "../../translate/Translate";
import SecondaryButton from "../button/SecondaryButton";

export const TitleAndInputGrid = styled.div`
    display: grid;
    grid-template-columns: 0.7fr 1.3fr;
    column-gap: 17px;
    row-gap: ${({ gap }) => gap || "17px"};
    width: 100%;
    box-sizing: border-box;
    align-items: ${(props) => props.alignItem || "center"};
    justify-content: flex-start;
`;

export const InputAndButtonGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 0.2fr;
    column-gap: 10px;
    width: 100%;
    box-sizing: border-box;
    align-items: ${(props) => props.alignItem || "center"};
    justify-content: flex-start;
`;

const StyledImageArea = styled.div`
    width: 100%;
    min-height: 180px;
    padding: 12px 16px;
    box-sizing: border-box;

    border: 1px solid #e6e6e9;
    border-radius: 8px;
    background-color: transparent;

    &::placeholder {
        font-size: ${(props) => props.placeHolderFontSize || Theme.text.c3.fontSize};
        font-family: ${(props) => props.placeHolderFontFamily || Theme.text.default.fontFamily};
        letter-spacing: ${(props) => props.placeHolderLetterSpacing || Theme.text.default.letterSpacing};
        line-height: ${(props) => props.placeHolderLineHeight || Theme.text.default.lineHeight};
        font-weight: ${(props) => props.placeHolderFontWeight || Theme.text.c3.fontWeight};
        color: ${(props) => props.placeHolderFontColor || Theme.color.gray500};
    }
`;

export const TitleAndInput = ({
    title,
    placeholder,
    type,
    dropdownValues,
    inputValue,
    setInputValue,
    disabled,
    hasDot,
    isRequired,
    inputs,
    tooltip,
    tooltipWidth,
    tooltipTop,
    gap,
    buttonIcon,
    buttonOnClick,
    ...props
}) => {
    const fileInputRef = useRef(null);

    const handleImageUploadClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const handleFileChange = (event) => {
        const files = event.target.files;
        if (files && files.length > 0) {
            setInputValue((prv) => {
                const prevArray = Array.isArray(prv) ? prv : [];
                // TODO: 추후 로깅 삭제
                console.log("디버깅: ", [...prevArray, ...Array.from(files)]);
                return [...prevArray, ...Array.from(files)];
            });
        }
    };

    const handleRemoveFile = (indexToRemove) => {
        setInputValue((prv) => {
            return prv.filter((_, index) => index !== indexToRemove);
        });
    };

    if (type === "textAria") {
        return (
            <Row justifyContent={"space-between"} width={"100%"}>
                <TitleAndInputGrid alignItem={"flex-start"} gap={gap}>
                    <Row alignItem={"center"}>
                        {hasDot && (
                            <>
                                <CustomIcon iconName={"purple_dot"} size={"6px"} />
                                <SizedBox width={"8px"} />
                            </>
                        )}
                        <C3Text>{title}</C3Text>
                    </Row>
                    <CustomInput
                        type={"textAria"}
                        placeholder={placeholder}
                        inputValue={inputValue}
                        setInputValue={setInputValue}
                        disabled={disabled}
                    />
                </TitleAndInputGrid>
            </Row>
        );
    } else if (type === "inputs") {
        return (
            <Row justifyContent={"space-between"} width={"100%"}>
                <TitleAndInputGrid alignItem={"flex-start"} gap={gap}>
                    <Row alignItem={"center"}>
                        {hasDot && (
                            <>
                                <CustomIcon iconName={"purple_dot"} size={"6px"} />
                                <SizedBox width={"8px"} />
                            </>
                        )}
                        <C3Text>{title}</C3Text>
                        {isRequired && (
                            <>
                                <SizedBox width={"4px"} />
                                <C3Text fontColor={Theme.color.rose500}>*</C3Text>
                            </>
                        )}
                    </Row>
                    {inputs.map((input, index) => (
                        <>
                            {index !== 0 && <SizedBox />}

                            <CustomInput
                                type={input.type}
                                placeholder={input.placeholder}
                                inputValue={input.inputValue}
                                setInputValue={input.setInputValue}
                                dropdownValues={input.dropdownValues}
                                disabled={input.disabled}
                            />
                        </>
                    ))}
                </TitleAndInputGrid>
            </Row>
        );
    } else if (type === "image") {
        return (
            <Row justifyContent={"space-between"} width={"100%"}>
                <TitleAndInputGrid alignItem={"flex-start"} gap={gap}>
                    <Row alignItem={"center"}>
                        {hasDot && (
                            <>
                                <CustomIcon iconName={"purple_dot"} size={"6px"} />
                                <SizedBox width={"8px"} />
                            </>
                        )}
                        <C3Text>{title}</C3Text>
                        {isRequired && (
                            <>
                                <SizedBox width={"4px"} />
                                <C3Text fontColor={Theme.color.rose500}>*</C3Text>
                            </>
                        )}
                        {tooltip && (
                            <>
                                <SizedBox width={"4px"} />
                                <CustomTooltip width={tooltipWidth} top={tooltipTop}>
                                    {tooltip}
                                </CustomTooltip>
                            </>
                        )}
                    </Row>
                    <StyledImageArea>
                        <input
                            type="file"
                            accept="image/*"
                            ref={fileInputRef}
                            style={{ display: "none" }}
                            multiple // 여러 파일 선택 가능하도록 속성 추가
                            onChange={handleFileChange}
                        />
                        <Row
                            alignItem={"flex-end"}
                            width={"100%"}
                            justifyContent={"flex-end"}
                            cursor={"pointer"}
                            onClick={handleImageUploadClick}
                        >
                            <CustomIcon iconName={"add"} size={"17px"} cursor={"pointer"} />
                            <SizedBox width={"7px"} />
                            <C4Text fontColor={Theme.color.zinc850} cursor={"pointer"}>
                                Add Image
                            </C4Text>
                        </Row>

                        {(!inputValue || inputValue.length === 0) && (
                            <Column
                                width={"100%"}
                                justifyContent={"center"}
                                alignItem={"center"}
                                onClick={handleImageUploadClick}
                                cursor={"pointer"}
                            >
                                <SizedBox height={"40px"} />
                                <CustomIcon iconName={"empty_image"} size={"24.5px"} />
                                <SizedBox height={"12px"} />
                                <C3Text fontColor={Theme.color.gray500}>{Translate("common.emptyImage")}</C3Text>
                            </Column>
                        )}
                        {inputValue && inputValue.length > 0 && (
                            <>
                                {inputValue.map((file, index) => (
                                    <Row width={"100%"} justifyContent={"space-between"}>
                                        <Row alignItem={"center"} width={"80%"}>
                                            <img
                                                key={index}
                                                src={URL.createObjectURL(file)}
                                                alt={file.name}
                                                style={{
                                                    width: "44px",
                                                    height: "44px",
                                                    objectFit: "contain",
                                                    borderRadius: "6px",
                                                }}
                                            />
                                            <SizedBox width={"12px"} />
                                            <SizedBox width={"160px"} height={"100%"}>
                                                <C3Text fontWeight={"400"} useEllipsis={false}>
                                                    {file.name}
                                                </C3Text>
                                            </SizedBox>
                                        </Row>
                                        <CustomIcon
                                            iconName={"close"}
                                            size={"16px"}
                                            cursor={"pointer"}
                                            onClick={() => handleRemoveFile(index)}
                                        />
                                    </Row>
                                ))}
                            </>
                        )}
                    </StyledImageArea>
                </TitleAndInputGrid>
            </Row>
        );
    } else if (type === "courseImage") {
        return (
            <Row justifyContent={"space-between"} width={"100%"}>
                <TitleAndInputGrid alignItem={"flex-start"} gap={gap}>
                    <Row alignItem={"center"}>
                        <C3Text>{title}</C3Text>
                        {isRequired && (
                            <>
                                <SizedBox width={"4px"} />
                                <C3Text fontColor={Theme.color.rose500}>*</C3Text>
                            </>
                        )}
                        {tooltip && (
                            <>
                                <SizedBox width={"4px"} />
                                <CustomTooltip width={tooltipWidth} top={tooltipTop}>
                                    {tooltip}
                                </CustomTooltip>
                            </>
                        )}
                    </Row>
                    <StyledImageArea>
                        <input
                            type="file"
                            accept="image/*"
                            ref={fileInputRef}
                            style={{ display: "none" }}
                            onChange={handleFileChange}
                        />
                        {(!inputValue || inputValue.length === 0) && (
                            <Column width={"100%"} justifyContent={"center"} alignItem={"center"}>
                                <SizedBox height={"40px"} />
                                <CustomIcon iconName={"empty_image"} size={"24.5px"} />
                                <SizedBox height={"12px"} />
                                <C3Text fontColor={Theme.color.gray500}>{"Please upload an image."}</C3Text>
                                <SizedBox height={"16px"} />
                                <SecondaryButton icon={"add"} text={"Add Image"} onClick={handleImageUploadClick} />
                            </Column>
                        )}
                        {inputValue && inputValue.length > 0 && (
                            <>
                                {inputValue.map((file, index) => (
                                    <Row width={"100%"} justifyContent={"space-between"}>
                                        <Row alignItem={"center"} width={"80%"}>
                                            <img
                                                key={index}
                                                src={URL.createObjectURL(file)}
                                                alt={file.name}
                                                style={{
                                                    width: "44px",
                                                    height: "44px",
                                                    objectFit: "contain",
                                                    borderRadius: "6px",
                                                }}
                                            />
                                            <SizedBox width={"12px"} />
                                            <SizedBox width={"160px"} height={"100%"}>
                                                <C3Text fontWeight={"400"} useEllipsis={false}>
                                                    {file.name}
                                                </C3Text>
                                            </SizedBox>
                                        </Row>
                                        <CustomIcon
                                            iconName={"close"}
                                            size={"16px"}
                                            cursor={"pointer"}
                                            onClick={() => handleRemoveFile(index)}
                                        />
                                    </Row>
                                ))}
                            </>
                        )}
                    </StyledImageArea>
                </TitleAndInputGrid>
            </Row>
        );
    } else {
        return (
            <Row justifyContent={"space-between"} width={"100%"}>
                <TitleAndInputGrid gap={gap}>
                    <Row alignItem={"center"}>
                        {hasDot && (
                            <>
                                <CustomIcon iconName={"purple_dot"} size={"6px"} />
                                <SizedBox width={"8px"} />
                            </>
                        )}
                        <C3Text>{title}</C3Text>
                        {isRequired && (
                            <>
                                <SizedBox width={"4px"} />
                                <C3Text fontColor={Theme.color.rose500}>*</C3Text>
                            </>
                        )}
                        {tooltip && (
                            <>
                                <SizedBox width={"4px"} />
                                <CustomTooltip width={tooltipWidth} top={tooltipTop}>
                                    {tooltip}
                                </CustomTooltip>
                            </>
                        )}
                    </Row>
                    {!buttonIcon && (
                        <CustomInput
                            type={type}
                            placeholder={placeholder}
                            inputValue={inputValue}
                            setInputValue={setInputValue}
                            dropdownValues={dropdownValues}
                            disabled={disabled}
                            {...props}
                        />
                    )}
                    {buttonIcon && (
                        <InputAndButtonGrid alignItem={"center"}>
                            <CustomInput
                                type={type}
                                placeholder={placeholder}
                                inputValue={inputValue}
                                setInputValue={setInputValue}
                                dropdownValues={dropdownValues}
                                disabled={disabled}
                                {...props}
                            />
                            <FullWidthButton
                                icon={buttonIcon}
                                onClick={buttonOnClick}
                                backgroundColor={Theme.color.white}
                                padding={"13px 0"}
                            />
                        </InputAndButtonGrid>
                    )}
                </TitleAndInputGrid>
            </Row>
        );
    }
};
